import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Camera, X, MapPin, Calendar, Phone, ArrowLeft } from '@phosphor-icons/react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EditSale() {
  const { saleId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [form, setForm] = useState(null);
  const [existingPhotos, setExistingPhotos] = useState([]);
  const [newPhotos, setNewPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    const fetchSale = async () => {
      try {
        const { data } = await axios.get(`${API}/sales/${saleId}`, { withCredentials: true });
        if (data.created_by !== user?._id && user?.role !== 'admin') {
          toast.error('You can only edit your own listings');
          navigate('/my-listings');
          return;
        }
        setForm({
          title: data.title || '', description: data.description || '',
          category: data.category || 'estate',
          address: data.address || '', city: data.city || '',
          state: data.state || '', zip_code: data.zip_code || '',
          lat: data.lat, lng: data.lng,
          start_date: data.start_date || '', end_date: data.end_date || '',
          contact_name: data.contact_name || '', contact_phone: data.contact_phone || '',
          contact_email: data.contact_email || '',
          items_categories: data.items_categories || [],
        });
        setExistingPhotos(data.photos || []);
      } catch {
        toast.error('Failed to load sale');
        navigate('/my-listings');
      } finally {
        setFetching(false);
      }
    };
    fetchSale();
  }, [saleId, user, navigate]);

  const updateField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handlePhotoSelect = (e) => {
    const files = Array.from(e.target.files);
    const photos = files.map(file => ({ file, preview: URL.createObjectURL(file), path: null }));
    setNewPhotos(prev => [...prev, ...photos]);
  };

  const removeExistingPhoto = (idx) => {
    setExistingPhotos(prev => prev.filter((_, i) => i !== idx));
  };

  const removeNewPhoto = (idx) => {
    setNewPhotos(prev => {
      const updated = [...prev];
      URL.revokeObjectURL(updated[idx].preview);
      updated.splice(idx, 1);
      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.address || !form.city || !form.state || !form.start_date || !form.end_date) {
      toast.error('Please fill in all required fields');
      return;
    }
    setLoading(true);
    try {
      // Upload new photos
      const uploadedPaths = [];
      for (const photo of newPhotos) {
        if (photo.path) { uploadedPaths.push(photo.path); continue; }
        const fd = new FormData();
        fd.append('file', photo.file);
        const { data } = await axios.post(`${API}/upload`, fd, { withCredentials: true });
        uploadedPaths.push(data.path);
      }

      const allPhotos = [...existingPhotos, ...uploadedPaths];

      // Geocode if address changed
      let lat = form.lat, lng = form.lng;
      const fullAddress = `${form.address}, ${form.city}, ${form.state} ${form.zip_code}`;
      try {
        const { data: geo } = await axios.get(`${API}/geocode?address=${encodeURIComponent(fullAddress)}`);
        if (geo.lat && geo.lng) { lat = geo.lat; lng = geo.lng; }
      } catch {}

      await axios.put(`${API}/sales/${saleId}`, {
        ...form, lat, lng, photo_paths: allPhotos, items_categories: form.items_categories,
      }, { withCredentials: true });

      toast.success('Listing updated!');
      navigate(`/sale/${saleId}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update listing');
    } finally {
      setLoading(false);
    }
  };

  if (fetching) return (
    <div className="flex items-center justify-center min-h-[calc(100vh-64px)]">
      <div className="text-xl font-bold text-[#004E89]">Loading...</div>
    </div>
  );

  if (!form) return null;

  return (
    <div className="min-h-[calc(100vh-64px)] bg-[#F9F9F6] px-4 py-8" data-testid="edit-sale-page">
      <div className="max-w-3xl mx-auto">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-[#5C7A99] hover:text-[#004E89] font-bold text-sm mb-6 transition-colors" data-testid="edit-back-btn">
          <ArrowLeft size={18} weight="bold" /> Back
        </button>

        <h1 className="text-3xl font-black text-[#004E89] mb-6" style={{ fontFamily: 'Outfit' }} data-testid="edit-sale-title">
          Edit Listing
        </h1>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info */}
          <div className="bg-white border-2 border-[#004E89] rounded-2xl p-6 shadow-[4px_4px_0px_#004E89]">
            <h2 className="text-xl font-bold text-[#004E89] mb-4" style={{ fontFamily: 'Outfit' }}>Sale Details</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-[#004E89] mb-1 uppercase tracking-wide">Title *</label>
                <input value={form.title} onChange={(e) => updateField('title', e.target.value)} className="input-neo" required data-testid="edit-title-input" />
              </div>
              <div>
                <label className="block text-sm font-bold text-[#004E89] mb-1 uppercase tracking-wide">Description</label>
                <textarea value={form.description} onChange={(e) => updateField('description', e.target.value)} className="input-neo min-h-[100px]" rows={4} data-testid="edit-description-input" />
              </div>
              <div>
                <label className="block text-sm font-bold text-[#004E89] mb-1 uppercase tracking-wide">Category *</label>
                <Select value={form.category} onValueChange={(val) => updateField('category', val)}>
                  <SelectTrigger className="border-2 border-[#004E89] rounded-xl shadow-[2px_2px_0px_#004E89] h-11" data-testid="edit-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="border-2 border-[#004E89] rounded-xl shadow-[4px_4px_0px_#004E89]">
                    <SelectItem value="estate">Estate Sale</SelectItem>
                    <SelectItem value="garage">Garage Sale</SelectItem>
                    <SelectItem value="auction">Auction</SelectItem>
                    <SelectItem value="moving">Moving Sale</SelectItem>
                    <SelectItem value="business_closing">Business Going Out</SelectItem>
                    <SelectItem value="craft_fair">Craft Fair</SelectItem>
                    <SelectItem value="food_vendor">Food Vendor</SelectItem>
                    <SelectItem value="carnival">Carnival</SelectItem>
                    <SelectItem value="vendor_event">Vendor Event</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Location */}
          <div className="bg-white border-2 border-[#004E89] rounded-2xl p-6 shadow-[4px_4px_0px_#004E89]">
            <h2 className="text-xl font-bold text-[#004E89] mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <MapPin size={24} weight="duotone" /> Location
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-[#004E89] mb-1 uppercase tracking-wide">Address *</label>
                <input value={form.address} onChange={(e) => updateField('address', e.target.value)} className="input-neo" required data-testid="edit-address-input" />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-bold text-[#004E89] mb-1 uppercase tracking-wide">City *</label>
                  <input value={form.city} onChange={(e) => updateField('city', e.target.value)} className="input-neo" required data-testid="edit-city-input" />
                </div>
                <div>
                  <label className="block text-sm font-bold text-[#004E89] mb-1 uppercase tracking-wide">State *</label>
                  <input value={form.state} onChange={(e) => updateField('state', e.target.value)} className="input-neo" required data-testid="edit-state-input" />
                </div>
                <div>
                  <label className="block text-sm font-bold text-[#004E89] mb-1 uppercase tracking-wide">Zip</label>
                  <input value={form.zip_code} onChange={(e) => updateField('zip_code', e.target.value)} className="input-neo" data-testid="edit-zip-input" />
                </div>
              </div>
            </div>
          </div>

          {/* Dates */}
          <div className="bg-white border-2 border-[#004E89] rounded-2xl p-6 shadow-[4px_4px_0px_#004E89]">
            <h2 className="text-xl font-bold text-[#004E89] mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <Calendar size={24} weight="duotone" /> Dates
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-bold text-[#004E89] mb-1 uppercase tracking-wide">Start Date *</label>
                <input type="date" value={form.start_date} onChange={(e) => updateField('start_date', e.target.value)} className="input-neo" required data-testid="edit-start-date" />
              </div>
              <div>
                <label className="block text-sm font-bold text-[#004E89] mb-1 uppercase tracking-wide">End Date *</label>
                <input type="date" value={form.end_date} onChange={(e) => updateField('end_date', e.target.value)} className="input-neo" required data-testid="edit-end-date" />
              </div>
            </div>
          </div>

          {/* Photos */}
          <div className="bg-white border-2 border-[#004E89] rounded-2xl p-6 shadow-[4px_4px_0px_#004E89]">
            <h2 className="text-xl font-bold text-[#004E89] mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <Camera size={24} weight="duotone" /> Photos
            </h2>

            {existingPhotos.length > 0 && (
              <div className="mb-4">
                <p className="text-xs font-bold text-[#5C7A99] uppercase mb-2">Current Photos</p>
                <div className="grid grid-cols-4 gap-3" data-testid="existing-photos-grid">
                  {existingPhotos.map((photo, idx) => (
                    <div key={idx} className="relative rounded-xl overflow-hidden border-2 border-[#004E89] h-24">
                      <img src={`${API}/files/${photo}`} alt="" className="w-full h-full object-cover" />
                      <button type="button" onClick={() => removeExistingPhoto(idx)} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center" data-testid={`remove-existing-photo-${idx}`}>
                        <X size={12} weight="bold" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="photo-upload-zone mb-4" onClick={() => fileInputRef.current?.click()} data-testid="edit-photo-upload">
              <Camera size={36} weight="duotone" className="mx-auto text-[#5C7A99] mb-2" />
              <p className="text-sm font-medium text-[#5C7A99]">Click to add more photos</p>
            </div>
            <input ref={fileInputRef} type="file" multiple accept="image/*" onChange={handlePhotoSelect} className="hidden" />

            {newPhotos.length > 0 && (
              <div className="grid grid-cols-4 gap-3" data-testid="new-photos-grid">
                {newPhotos.map((photo, idx) => (
                  <div key={idx} className="relative rounded-xl overflow-hidden border-2 border-[#4CB944] h-24">
                    <img src={photo.preview} alt="" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => removeNewPhoto(idx)} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center" data-testid={`remove-new-photo-${idx}`}>
                      <X size={12} weight="bold" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Contact */}
          <div className="bg-white border-2 border-[#004E89] rounded-2xl p-6 shadow-[4px_4px_0px_#004E89]">
            <h2 className="text-xl font-bold text-[#004E89] mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <Phone size={24} weight="duotone" /> Contact Info
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-[#004E89] mb-1 uppercase tracking-wide">Name</label>
                <input value={form.contact_name} onChange={(e) => updateField('contact_name', e.target.value)} className="input-neo" data-testid="edit-contact-name" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-[#004E89] mb-1 uppercase tracking-wide">Phone</label>
                  <input value={form.contact_phone} onChange={(e) => updateField('contact_phone', e.target.value)} className="input-neo" data-testid="edit-contact-phone" />
                </div>
                <div>
                  <label className="block text-sm font-bold text-[#004E89] mb-1 uppercase tracking-wide">Email</label>
                  <input value={form.contact_email} onChange={(e) => updateField('contact_email', e.target.value)} className="input-neo" data-testid="edit-contact-email" />
                </div>
              </div>
            </div>
          </div>

          <button type="submit" disabled={loading} className="btn-primary w-full py-4 text-lg" data-testid="edit-submit-btn">
            {loading ? 'Saving Changes...' : 'Save Changes'}
          </button>
        </form>
      </div>
    </div>
  );
}
